import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { AsyncLoadPagedResult, AsyncLoadPagedOptions } from '../../../shared/abstractStore';
import { useFetchMoreCallback } from '../../../shared/useFetchMoreCallback';
import { subscriptionsQuery, subscriptionsQueryVariables } from '../generated/subscriptionsQuery';
import { mainApiConfig } from '../../../configure/mainApiConfig';
import { subscriptionFieldsFragment } from '../models/Subscription';
import { SubscriptionType } from '../models/codeOnly/SubscriptionType';

export interface SubscriptionsAsyncLoadPageOptions extends AsyncLoadPagedOptions {
    subscriptionTypes?: Array<SubscriptionType>,
}

/**
 * Get a list of subscriptions from the store.
 * @param id
 * @param options
 */
export function useSubscriptions(options: SubscriptionsAsyncLoadPageOptions = {}): AsyncLoadPagedResult<subscriptionsQuery> {
    // Query data we need from the api.
    const [load, { data, loading, error, refetch, fetchMore: fetchMoreFromStore }] = useLazyQuery<subscriptionsQuery, subscriptionsQueryVariables>(
        gql`
        query subscriptionsQuery ($offset: Int, $first: Int, $subscriptionTypes: [String!]) {
            items: subscriptions (offset: $offset, first: $first, subscriptionTypes: $subscriptionTypes) {
                ...subscriptionFields
            }
        }

        ${subscriptionFieldsFragment}
        `,
        {

            variables: {
                offset: options.startOffset,
                first: options.pageSize,
                subscriptionTypes: options.subscriptionTypes,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Get the functions used to manage paging.
    const { fetchMore, hasMore: _hasMore } = useFetchMoreCallback<subscriptionsQuery>(
        fetchMoreFromStore,
        (options?.startOffset ?? 0) + (data?.items?.length ?? 0),
        (prevResults, newResults) => ({
            ...prevResults,
            ...newResults,

            items: [...(prevResults?.items ?? []), ...(newResults?.items ?? [])]
        }),
        (newResults) => newResults?.items?.length
    );
    const hasMore = React.useCallback(() => !options.pageSize ? false : _hasMore(), [options.pageSize, _hasMore]);
    
    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error,
        fetchMore: fetchMore,
        hasMore: hasMore,
    }), [data, refresh, loading, error, fetchMore, hasMore]);
}
